import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../AuthContext";
import s from "../../admin_styles/News.module.css";

const initialForm = {
	title: "",
	summary: "",
	content: "",
	news_type: "general",
	dashboard_slot: "",
	is_published: true,
	published_at: "",
};

const NEWS_CHIP_MAP = {
	headline: { key: "headline", label: "Главное обновление" },
	ui: { key: "ui", label: "Интерфейс" },
	integration: { key: "integration", label: "Интеграции" },
	account: { key: "account", label: "Аккаунт" },
	general: { key: "ui", label: "Общая новость" },
};

const SLOT_OPTIONS = [
	{ value: "", label: "Не показывать на дашборде" },
	{ value: "1", label: "Слот 1 (главная)" },
	{ value: "2", label: "Слот 2" },
	{ value: "3", label: "Слот 3" },
	{ value: "4", label: "Слот 4" },
	{ value: "5", label: "Слот 5" },
];

const formatDateTimeForInput = (value) => {
	if (!value) {
		return "";
	}

	const date = new Date(value);
	if (!Number.isNaN(date.getTime())) {
		const pad = (num) => String(num).padStart(2, "0");
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
	}

	return String(value).slice(0, 16);
};

const formatDateTimeForApi = (value) => {
	if (!value) {
		return null;
	}

	return value.length === 16 ? `${value}:00` : value;
};

const resolveChipMeta = (newsType) => {
	return NEWS_CHIP_MAP[newsType] || NEWS_CHIP_MAP.general;
};

export default function News() {
	const { role } = useContext(AuthContext);
	const [formData, setFormData] = useState(initialForm);
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const isSuperadminRole = role === "superadmin";

	const loadItems = async () => {
		setIsLoading(true);
		setError("");
		try {
			const response = await fetch("/api/news/admin/list", {
				credentials: "include",
			});
			const result = await response.json().catch(() => ({}));
			if (!response.ok || !result.ok) {
				throw new Error(result.error || `Ошибка ${response.status}`);
			}
			setItems(Array.isArray(result.items) ? result.items : []);
		} catch (loadError) {
			setError(loadError.message || "Не удалось загрузить новости.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!isSuperadminRole) {
			return;
		}
		loadItems();
	}, [isSuperadminRole]);

	const handleChange = (event) => {
		const { name, value, type, checked } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError("");
		setSuccess("");

		if (!formData.title.trim()) {
			setError("Укажите заголовок новости.");
			return;
		}

		if (!formData.content.trim()) {
			setError("Укажите текст новости.");
			return;
		}

		setIsSaving(true);

		try {
			const publishedAt = formatDateTimeForApi(formData.published_at);
			const normalizedSlot = formData.dashboard_slot === "" ? null : Number(formData.dashboard_slot);
			const response = await fetch(editingId ? `/api/news/${editingId}` : "/api/news/", {
				method: editingId ? "PUT" : "POST",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				body: JSON.stringify({
					title: formData.title.trim(),
					summary: formData.summary.trim(),
					content: formData.content.trim(),
					news_type: formData.news_type,
					dashboard_slot: normalizedSlot,
					is_published: formData.is_published,
					...(publishedAt ? { published_at: publishedAt } : {}),
				}),
			});

			const result = await response.json().catch(() => ({}));
			if (!response.ok || !result.ok) {
				throw new Error(result.error || `Ошибка ${response.status}`);
			}

			const savedItem = result.item || null;
			if (savedItem) {
				setItems((prev) => {
					const filtered = prev.filter((item) => item.id !== savedItem.id);
					return [savedItem, ...filtered];
				});
			}
			setSuccess(editingId ? "Новость обновлена." : "Новость сохранена в базу.");
			setFormData(initialForm);
			setEditingId(null);
		} catch (submitError) {
			setError(submitError.message || "Не удалось сохранить новость.");
		} finally {
			setIsSaving(false);
		}
	};

	const startEdit = (item) => {
		setError("");
		setSuccess("");
		setEditingId(item.id);
		setFormData({
			title: item.title || "",
			summary: item.summary || "",
			content: item.content || "",
			news_type: item.news_type || "general",
			dashboard_slot: item.dashboard_slot != null ? String(item.dashboard_slot) : "",
			is_published: Boolean(item.is_published),
			published_at: formatDateTimeForInput(item.published_at || item.created_at),
		});
	};

	const resetForm = () => {
		setEditingId(null);
		setFormData(initialForm);
		setError("");
		setSuccess("");
	};

	const handleDelete = async (item) => {
		if (!window.confirm(`Удалить новость "${item.title}"?`)) {
			return;
		}

		setError("");
		setSuccess("");
		try {
			const response = await fetch(`/api/news/${item.id}`, {
				method: "DELETE",
				credentials: "include",
			});
			const result = await response.json().catch(() => ({}));
			if (!response.ok || !result.ok) {
				throw new Error(result.error || `Ошибка ${response.status}`);
			}
			setItems((prev) => prev.filter((newsItem) => newsItem.id !== item.id));
			if (editingId === item.id) {
				resetForm();
			}
			setSuccess("Новость удалена.");
		} catch (deleteError) {
			setError(deleteError.message || "Не удалось удалить новость.");
		}
	};

	if (!isSuperadminRole) {
		return <div className={s.info}>Доступ к управлению новостями есть только у суперадмина.</div>;
	}

	const selectedChip = resolveChipMeta(formData.news_type);

	return (
		<div className={s.wrap}>
			<form className={s.form} onSubmit={handleSubmit}>
				<div className={s.head}>{editingId ? "Редактирование новости" : "Публикация новости"}</div>

				<label className={s.field}>
					<span className={s.label}>Заголовок</span>
					<input
						className={s.input}
						name="title"
						value={formData.title}
						onChange={handleChange}
						placeholder="Заголовок новости"
					/>
				</label>

				<label className={s.field}>
					<span className={s.label}>Короткое описание</span>
					<textarea
						className={s.textareaSmall}
						name="summary"
						value={formData.summary}
						onChange={handleChange}
						placeholder="Короткое описание для списка"
					/>
				</label>

				<label className={s.field}>
					<span className={s.label}>Дата и время публикации</span>
					<input
						type="datetime-local"
						className={s.input}
						name="published_at"
						value={formData.published_at}
						onChange={handleChange}
					/>
				</label>

				<label className={s.field}>
					<span className={s.label}>Тип новости</span>
					<select
						className={s.input}
						name="news_type"
						value={formData.news_type}
						onChange={handleChange}
					>
						{Object.entries(NEWS_CHIP_MAP).map(([value, meta]) => (
							<option key={value} value={value}>
								{meta.label}
							</option>
						))}
					</select>
				</label>

				<div className={s.field}>
					<span className={s.label}>Предпросмотр тега</span>
					<div>
						<span className={`${s.tag} ${s[selectedChip.key]}`}>{selectedChip.label}</span>
					</div>
				</div>

				<label className={s.field}>
					<span className={s.label}>Слот на дашборде</span>
					<select
						className={s.input}
						name="dashboard_slot"
						value={formData.dashboard_slot}
						onChange={handleChange}
					>
						{SLOT_OPTIONS.map((slotOption) => (
							<option key={slotOption.value || "none"} value={slotOption.value}>
								{slotOption.label}
							</option>
						))}
					</select>
				</label>

				<label className={s.field}>
					<span className={s.label}>Полный текст</span>
					<textarea
						className={s.textarea}
						name="content"
						value={formData.content}
						onChange={handleChange}
						placeholder="Полный текст новости"
					/>
				</label>

				<label className={s.checkboxRow}>
					<input
						type="checkbox"
						name="is_published"
						checked={formData.is_published}
						onChange={handleChange}
					/>
					<span>Опубликовать сразу</span>
				</label>

				{error ? <div className={s.error}>{error}</div> : null}
				{success ? <div className={s.success}>{success}</div> : null}

				<div className={s.actions}>
					<button type="submit" className={s.primaryButton} disabled={isSaving}>
						{isSaving ? "Сохранение..." : editingId ? "Сохранить изменения" : "Сохранить новость"}
					</button>
					{editingId ? (
						<button type="button" className={s.secondaryButton} onClick={resetForm} disabled={isSaving}>
							Отмена
						</button>
					) : null}
				</div>
			</form>

			<div className={s.preview}>
				<div className={s.previewHead}>Все новости</div>
				{isLoading ? <div className={s.empty}>Загрузка...</div> : null}
				{!isLoading && !items.length ? <div className={s.empty}>Новостей пока нет.</div> : null}
				<div className={s.list}>
					{items.map((item) => (
						<div key={item.id} className={s.newsCard}>
							<div className={s.newsCardTop}>
								<div>
									<div className={s.previewTitle}>{item.title}</div>
									<div className={s.metaLine}>
										<span className={item.is_published ? s.statusPublished : s.statusDraft}>
											{item.is_published ? "Опубликовано" : "Скрыто"}
										</span>
											<span className={`${s.tag} ${s[resolveChipMeta(item.news_type).key]}`}>
												{resolveChipMeta(item.news_type).label}
											</span>
											<span>
												{item.dashboard_slot ? `Слот ${item.dashboard_slot}` : "Без слота"}
											</span>
										<span>{item.published_at || item.created_at || ""}</span>
									</div>
								</div>
								<div className={s.inlineButtons}>
									<button type="button" className={s.secondaryButton} onClick={() => startEdit(item)}>
										Редактировать
									</button>
									<button type="button" className={s.dangerButton} onClick={() => handleDelete(item)}>
										Удалить
									</button>
								</div>
							</div>
							{item.summary ? <div className={s.previewSummary}>{item.summary}</div> : null}
							<div className={s.previewContent}>{item.content}</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
